import { parseMindsetCustomerSdkDownload, type MindsetCustomerSdkDownload } from "@mindset/api-client";

export class SdkArchiveError extends Error {
  constructor(readonly reason: "expired" | "denied" | "integrity" | "unavailable") {
    super(`SDK archive ${reason}`);
  }
}

const MAX_ARCHIVE_BYTES = 20 * 1024 * 1024;

export async function fetchSdkArchive(grant: MindsetCustomerSdkDownload, signal: AbortSignal, request: typeof fetch = fetch): Promise<Blob> {
  signal.throwIfAborted();
  const verified = parseMindsetCustomerSdkDownload(grant, { releaseId: grant.releaseId, assetId: "typescript-bundle" });
  const deadline = Date.parse(verified.expiresAt);
  const ensureFresh = () => {
    if (deadline <= Date.now()) throw new SdkArchiveError("expired");
  };
  ensureFresh();
  if (verified.bytes > MAX_ARCHIVE_BYTES) throw new SdkArchiveError("integrity");
  const headers = new Headers();
  for (const [name, value] of Object.entries(verified.requiredHeaders ?? {})) {
    if (name.toLowerCase() !== "if-match" || headers.has(name) || !/^"[A-Za-z0-9_-]{1,128}"$/.test(value)) {
      throw new SdkArchiveError("integrity");
    }
    headers.set(name, value);
  }
  const signedHeaders = new URL(verified.url).searchParams.get("X-Amz-SignedHeaders")?.toLowerCase().split(";") ?? [];
  if (signedHeaders.includes("if-match") && !headers.has("if-match")) throw new SdkArchiveError("integrity");
  const controller = new AbortController();
  const abort = () => controller.abort(signal.reason);
  signal.addEventListener("abort", abort, { once: true });
  const timeout = setTimeout(() => controller.abort(), 120_000);
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  try {
    const response = await request(verified.url, { method: "GET", headers, signal: controller.signal,
      credentials: "omit", redirect: "error", cache: "no-store", referrerPolicy: "no-referrer" });
    signal.throwIfAborted();
    if (response.status === 401 || response.status === 403) throw new SdkArchiveError("denied");
    if (response.status === 412) throw new SdkArchiveError("integrity");
    if (response.status !== 200 || !response.body) throw new SdkArchiveError("unavailable");
    reader = response.body.getReader();
    controller.signal.addEventListener("abort", () => { void reader?.cancel().catch(() => {}); }, { once: true });
    const contentLength = response.headers.get("content-length");
    const encoding = response.headers.get("content-encoding");
    if (response.headers.has("content-range") || (contentLength !== null && contentLength !== String(verified.bytes)) || (encoding && encoding !== "identity")) {
      throw new SdkArchiveError("integrity");
    }
    const chunks: Uint8Array[] = [];
    let size = 0;
    while (true) {
      const { done, value } = await reader.read();
      controller.signal.throwIfAborted();
      ensureFresh();
      if (done) break;
      size += value.byteLength;
      if (size > verified.bytes) throw new SdkArchiveError("integrity");
      chunks.push(new Uint8Array(value));
    }
    if (size !== verified.bytes) throw new SdkArchiveError("integrity");
    const bytes = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    controller.signal.throwIfAborted();
    ensureFresh();
    const sha256 = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
    if (sha256 !== verified.sha256) throw new SdkArchiveError("integrity");
    return new Blob([bytes], { type: "application/zip" });
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener("abort", abort);
    await reader?.cancel().catch(() => {});
    reader?.releaseLock();
    controller.abort();
  }
}

export function sdkArchiveErrorMessage(error: unknown): string {
  const status = error && typeof error === "object" && "status" in error ? error.status : undefined;
  if (status === 401 || status === 403 || (error instanceof SdkArchiveError && error.reason === "denied")) {
    return "SDK download was denied or its link expired. Check owner access and activation, then retry for a fresh link.";
  }
  if ((error instanceof SdkArchiveError && error.reason === "expired") ||
      (error instanceof TypeError && error.message === "Expired SDK download")) {
    return "SDK download link expired. Retry to request a fresh link.";
  }
  if (error instanceof SdkArchiveError && error.reason === "integrity") {
    return "SDK archive verification failed. No file was saved. Refresh the application before retrying.";
  }
  return "SDK ZIP could not be downloaded. Verify activation and try again.";
}
